import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Btn, PillTag, Avatar } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  useStoreMutation,
  mutations,
  type LiveCategory,
  type Tier,
} from "@/lib/store";

type Mode = "now" | "later";

/** Pad a number to a 2-char string (used for date/time inputs) */
function pad(n: number) { return n.toString().padStart(2, "0"); }
/** Default future date: tomorrow at 6:30 PM, formatted for datetime-local */
function defaultFutureDateTime() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(18, 30, 0, 0);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const CATEGORIES: { key: LiveCategory; label: string; emoji: string }[] = [
  { key: "strength", label: "Strength",  emoji: "💪" },
  { key: "yoga",     label: "Yoga",      emoji: "🧘" },
  { key: "cardio",   label: "Cardio",    emoji: "🏃" },
  { key: "recovery", label: "Recovery",  emoji: "🌿" },
  { key: "prayer",   label: "Prayer",    emoji: "🙏" },
  { key: "teaching", label: "Teaching",  emoji: "📖" },
];

const TIERS: { key: Tier; label: string; sub: string }[] = [
  { key: "Seedling",    label: "All sisters",      sub: "Open to every tier" },
  { key: "Rooted",      label: "Rooted+",          sub: "Most chosen" },
  { key: "Flourishing", label: "Flourishing only", sub: "Inner circle" },
];

export default function GoLive() {
  const [, setLocation] = useLocation();
  const schedule = useStoreMutation(mutations.scheduleLiveClass);
  const start = useStoreMutation(mutations.startLiveClass);

  const [mode, setMode] = useState<Mode>("now");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<LiveCategory>("strength");
  const [duration, setDuration] = useState(30);
  const [tier, setTier] = useState<Tier>("Rooted");
  const [verse, setVerse] = useState("");
  const [scheduledAt, setScheduledAt] = useState<string>(defaultFutureDateTime());

  const [checks, setChecks] = useState({ camera: true, mic: true, internet: true });

  const scheduledFutureMs = useMemo(() => new Date(scheduledAt).getTime(), [scheduledAt]);
  const scheduledIsValid = mode === "now" || (Number.isFinite(scheduledFutureMs) && scheduledFutureMs > Date.now());
  const liveChecksOk = mode === "later" || (checks.camera && checks.mic && checks.internet);
  const ready = title.trim().length > 0 && liveChecksOk && scheduledIsValid;

  const submit = () => {
    if (!ready) return;
    const thumbForCategory: Record<LiveCategory, string> = {
      strength: "💪", yoga: "🧘", cardio: "🏃",
      recovery: "🌿", prayer: "🙏", teaching: "📖",
    };
    const cls = schedule({
      coachId: "u_kiley",
      title: title.trim(),
      category,
      description: description.trim() || `A ${duration}-minute ${category} session, live with Coach Kiley.`,
      scheduledFor: mode === "now" ? Date.now() : scheduledFutureMs,
      durationMin: duration,
      status: mode === "now" ? "live" : "upcoming",
      thumbnail: thumbForCategory[category],
      verseRef: verse.trim() || undefined,
      tierMin: tier,
    });
    if (mode === "now") {
      start(cls.id);
      setLocation(`/live/${cls.id}`);
    } else {
      setLocation("/coach");
    }
  };

  // Friendly relative-time hint shown next to scheduled time picker
  const scheduleHint = (() => {
    if (mode !== "later" || !Number.isFinite(scheduledFutureMs)) return "";
    const diff = scheduledFutureMs - Date.now();
    if (diff <= 0) return "Pick a future time, sister.";
    const mins = Math.round(diff / 60000);
    if (mins < 60) return `Sisters notified in ${mins} min`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `Sisters notified in ${hrs}h`;
    const days = Math.round(hrs / 24);
    return `Sisters notified ${days}d ahead`;
  })();

  return (
    <div
      className="flex flex-col min-h-full"
      style={{
        background:
          "linear-gradient(180deg, rgba(30,74,53,0.97) 0%, rgba(22,56,40,1) 100%)",
        color: "#F5F0E8",
      }}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-5 py-4 shrink-0"
        style={{ borderBottom: "1px solid rgba(245,240,232,0.08)" }}
      >
        <button
          onClick={() => setLocation("/coach")}
          data-testid="button-cancel-golive"
          className="flex items-center justify-center"
          style={{ width: 36, height: 36, borderRadius: 999, background: "rgba(245,240,232,0.1)", color: "#F5F0E8", border: "none", cursor: "pointer" }}
          aria-label="Cancel"
        >
          <Icon name="close" size={18} />
        </button>
        <div className="text-center">
          <p className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)", fontSize: 9.5 }}>Coach broadcast</p>
          <p className="mnf-serif italic" style={{ fontSize: 16, color: "#F5F0E8" }}>
            {mode === "now" ? "Go live" : "Schedule a class"}
          </p>
        </div>
        <div style={{ width: 36 }} />
      </div>

      <div className="flex-1 p-5 space-y-4">
        {/* Mode toggle */}
        <div
          className="flex gap-1 p-1"
          style={{ background: "rgba(245,240,232,0.06)", borderRadius: 999, border: "1px solid rgba(245,240,232,0.10)" }}
        >
          {([
            { key: "now" as const, label: "Go live now", icon: "videocam" },
            { key: "later" as const, label: "Schedule for later", icon: "event" },
          ]).map((m) => {
            const active = mode === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                data-testid={`button-mode-${m.key}`}
                className="flex-1 flex items-center justify-center gap-1.5 mnf-body transition-all"
                style={{
                  padding: "10px 12px", borderRadius: 999,
                  background: active ? "#F5F0E8" : "transparent",
                  color: active ? "var(--mnf-fd)" : "rgba(245,240,232,0.75)",
                  fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em",
                  border: "none", cursor: "pointer",
                }}
              >
                <Icon name={m.icon} size={14} weight={500} />
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Coach card */}
        <div className="flex items-center gap-3 mb-2">
          <Avatar initials="K" size={44} ringColor="rgba(229,185,151,0.55)" />
          <div className="flex-1">
            <p className="mnf-serif" style={{ fontSize: 17, fontWeight: 500, color: "#F5F0E8" }}>Coach Kiley</p>
            <p className="mnf-body italic" style={{ fontSize: 11.5, color: "rgba(245,240,232,0.65)" }}>
              {mode === "now" ? "Tending the garden, ready to teach." : "Plant a class for your sisters to find."}
            </p>
          </div>
          {mode === "now" ? (
            <PillTag tone="coral">● Ready</PillTag>
          ) : (
            <PillTag tone="purple">Scheduling</PillTag>
          )}
        </div>

        {/* Pre-flight (live only) */}
        {mode === "now" && (
          <div
            style={{
              borderRadius: 22,
              padding: 16,
              background: "rgba(245,240,232,0.06)",
              border: "1px solid rgba(245,240,232,0.10)",
            }}
          >
            <p className="mnf-eyebrow mb-3" style={{ color: "rgba(229,185,151,0.95)" }}>Pre-flight</p>
            <div className="space-y-2">
              <Check label="Camera" ok={checks.camera} onToggle={() => setChecks({ ...checks, camera: !checks.camera })} icon="videocam" />
              <Check label="Microphone" ok={checks.mic} onToggle={() => setChecks({ ...checks, mic: !checks.mic })} icon="mic" />
              <Check label="Connection · Stable" ok={checks.internet} onToggle={() => setChecks({ ...checks, internet: !checks.internet })} icon="wifi" />
            </div>
          </div>
        )}

        {/* Scheduled time (later only) */}
        {mode === "later" && (
          <div
            style={{
              borderRadius: 22, padding: 16,
              background: "rgba(127,119,221,0.10)",
              border: "1px solid rgba(127,119,221,0.30)",
            }}
          >
            <p className="mnf-eyebrow mb-2" style={{ color: "rgba(180,172,235,0.95)" }}>When</p>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              data-testid="input-scheduled-at"
              className="w-full mnf-serif outline-none"
              style={{
                background: "rgba(0,0,0,0.25)", color: "#F5F0E8",
                fontSize: 16, padding: "10px 12px", borderRadius: 12,
                border: "1px solid rgba(245,240,232,0.18)",
                colorScheme: "dark",
              }}
            />
            <p className="mnf-body italic mt-2" style={{ fontSize: 11.5, color: "rgba(245,240,232,0.65)" }}>
              {scheduleHint}
            </p>
          </div>
        )}

        {/* Title */}
        <Field label="Class title">
          <input
            data-testid="input-class-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Strength &amp; Grace · Lower body"
            className="w-full mnf-serif italic outline-none"
            style={{
              background: "transparent", color: "#F5F0E8",
              fontSize: 22, paddingBottom: 8,
              borderBottom: "1px solid rgba(245,240,232,0.18)",
            }}
          />
        </Field>

        {/* Description */}
        <Field label="A note for sisters">
          <textarea
            data-testid="input-class-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What we'll move through, what to grab from the kitchen…"
            rows={3}
            className="w-full mnf-body italic outline-none resize-none"
            style={{
              background: "transparent", color: "#F5F0E8",
              fontSize: 13, lineHeight: 1.5,
              padding: 12, borderRadius: 14,
              border: "1px solid rgba(245,240,232,0.14)",
            }}
          />
        </Field>

        {/* Category */}
        <Field label="Category">
          <div className="grid grid-cols-3 gap-2">
            {CATEGORIES.map((c) => {
              const active = category === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  data-testid={`button-cat-${c.key}`}
                  className="mnf-body transition-all"
                  style={{
                    padding: "10px 6px", borderRadius: 14,
                    background: active ? "rgba(192,120,74,0.30)" : "rgba(245,240,232,0.06)",
                    border: `1px solid ${active ? "rgba(192,120,74,0.6)" : "rgba(245,240,232,0.12)"}`,
                    color: "#F5F0E8", fontSize: 12, fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 18 }}>{c.emoji}</div>
                  {c.label}
                </button>
              );
            })}
          </div>
        </Field>

        {/* Duration */}
        <Field label={`Duration · ${duration} min`}>
          <input
            type="range"
            min={10}
            max={75}
            step={5}
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value, 10))}
            data-testid="input-duration"
            className="w-full"
            style={{ accentColor: "#C0784A" }}
          />
        </Field>

        {/* Tier */}
        <Field label="Who can join">
          <div className="grid grid-cols-3 gap-2">
            {TIERS.map((t) => {
              const active = tier === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTier(t.key)}
                  data-testid={`button-tier-${t.key.toLowerCase()}`}
                  className="text-center transition-all mnf-body"
                  style={{
                    padding: "10px 8px", borderRadius: 14,
                    background: active ? "var(--mnf-t)" : "rgba(245,240,232,0.06)",
                    border: `1px solid ${active ? "var(--mnf-t)" : "rgba(245,240,232,0.12)"}`,
                    color: "#F5F0E8",
                    cursor: "pointer",
                  }}
                >
                  <p className="mnf-serif" style={{ fontSize: 13, fontWeight: 500 }}>{t.label}</p>
                  <p style={{ fontSize: 10, opacity: 0.75, marginTop: 1 }}>{t.sub}</p>
                </button>
              );
            })}
          </div>
        </Field>

        {/* Verse */}
        <Field label="Anchor verse (optional)">
          <input
            data-testid="input-verse"
            value={verse}
            onChange={(e) => setVerse(e.target.value)}
            placeholder="Isaiah 40:31"
            className="w-full mnf-body italic outline-none"
            style={{
              background: "transparent", color: "#F5F0E8",
              fontSize: 14, paddingBottom: 8,
              borderBottom: "1px solid rgba(245,240,232,0.14)",
            }}
          />
        </Field>
      </div>

      {/* Sticky footer */}
      <div
        className="px-5 py-4 shrink-0"
        style={{
          background: "rgba(0,0,0,0.35)",
          borderTop: "1px solid rgba(245,240,232,0.08)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Btn
          variant={mode === "now" ? "coral" : "primary"}
          block
          size="lg"
          onClick={submit}
          disabled={!ready}
          data-testid={mode === "now" ? "button-go-live-now" : "button-schedule-class"}
          style={{ opacity: ready ? 1 : 0.55 }}
        >
          <Icon name={mode === "now" ? "videocam" : "event_available"} size={16} weight={500} />
          {mode === "now" ? "Go live now" : "Add to schedule"}
        </Btn>
        <p className="mnf-body italic mt-2 text-center" style={{ fontSize: 11, color: "rgba(245,240,232,0.5)" }}>
          {mode === "now"
            ? "Sisters will be notified the moment you begin."
            : "Sisters who tap Remind me will be notified beforehand."}
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mnf-eyebrow mb-2" style={{ color: "rgba(229,185,151,0.95)" }}>{label}</p>
      {children}
    </div>
  );
}

function Check({ label, ok, onToggle, icon }: { label: string; ok: boolean; onToggle: () => void; icon: string }) {
  return (
    <button
      onClick={onToggle}
      data-testid={`check-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="w-full flex items-center gap-3 mnf-body transition-all"
      style={{
        padding: "10px 12px", borderRadius: 12,
        background: ok ? "rgba(168,212,184,0.14)" : "rgba(192,120,74,0.14)",
        border: `1px solid ${ok ? "rgba(168,212,184,0.35)" : "rgba(192,120,74,0.4)"}`,
        color: "#F5F0E8", fontSize: 12.5, fontWeight: 600,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <Icon name={icon} size={16} />
      <span className="flex-1">{label}</span>
      <Icon name={ok ? "check_circle" : "error"} size={16} weight={500} />
    </button>
  );
}
